import { supabase } from '../lib/supabase';

export async function createAlert(alertData) {
  try {
    const { data, error } = await supabase
      ?.from('admin_alerts')
      ?.insert({
        alert_type: alertData?.alertType,
        severity: alertData?.severity,
        title: alertData?.title,
        message: alertData?.message,
        metadata: alertData?.metadata || {},
        user_id: alertData?.userId || null,
        case_id: alertData?.caseId || null,
        contract_id: alertData?.contractId || null
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Create alert error:', error);
    return { success: false, error: error?.message };
  }
}

export async function checkNegativeMarginAccounts() {
  try {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString();

    const usersResult = await supabase
      ?.from('user_profiles')
      ?.select('id, email, full_name')
      ?.eq('role', 'client');

    if (usersResult?.error) throw usersResult?.error;

    const negativeMarginUsers = [];

    for (const user of usersResult?.data || []) {
      const userPayments = await supabase
        ?.from('payments')
        ?.select('amount')
        ?.eq('user_id', user?.id)
        ?.eq('payment_status', 'succeeded')
        ?.gte('created_at', thirtyDaysAgo);

      const userRevenue = userPayments?.data?.reduce((sum, p) => sum + parseFloat(p?.amount || 0), 0) || 0;

      const userCases = await supabase
        ?.from('cases')
        ?.select('id')
        ?.eq('user_id', user?.id);

      const caseIds = userCases?.data?.map(c => c?.id) || [];

      if (caseIds?.length > 0) {
        const userAIRuns = await supabase
          ?.from('ai_runs')
          ?.select('cost_usd')
          ?.in('case_id', caseIds)
          ?.gte('created_at', thirtyDaysAgo);

        const userAICost = userAIRuns?.data?.reduce((sum, r) => sum + parseFloat(r?.cost_usd || 0), 0) || 0;
        const stripeFees = userRevenue * 0.029 + 0.30;
        const userMargin = userRevenue - userAICost - stripeFees;

        if (userMargin < 0) {
          negativeMarginUsers?.push({
            userId: user?.id,
            email: user?.email,
            fullName: user?.full_name,
            revenue: userRevenue,
            aiCost: userAICost,
            margin: userMargin
          });

          await createAlert({
            alertType: 'negative_margin',
            severity: 'high',
            title: `Negative Margin Account: ${user?.email}`,
            message: `Account ${user?.email} has negative margin of $${Math.abs(userMargin)?.toFixed(2)}. Revenue: $${userRevenue?.toFixed(2)}, AI Cost: $${userAICost?.toFixed(2)}`,
            metadata: {
              revenue: userRevenue,
              aiCost: userAICost,
              margin: userMargin,
              stripeFees
            },
            userId: user?.id
          });
        }
      }
    }

    return { success: true, data: negativeMarginUsers };
  } catch (error) {
    console.error('Check negative margin accounts error:', error);
    return { success: false, error: error?.message };
  }
}

export async function checkAICostSpikes() {
  try {
    const metricsResult = await supabase
      ?.from('usage_metrics_daily')
      ?.select('p95_ai_cost_per_report, avg_ai_cost_per_report')
      ?.order('metric_date', { ascending: false })
      ?.limit(7);

    if (metricsResult?.error) throw metricsResult?.error;

    if (!metricsResult?.data || metricsResult?.data?.length < 2) {
      return { success: true, data: [] };
    }

    const latestP95 = metricsResult?.data?.[0]?.p95_ai_cost_per_report;
    const avgP95 = metricsResult?.data?.slice(1)?.reduce((sum, m) => sum + parseFloat(m?.p95_ai_cost_per_report || 0), 0) / (metricsResult?.data?.length - 1);

    const spikeThreshold = avgP95 * 1.5;

    if (latestP95 > spikeThreshold) {
      await createAlert({
        alertType: 'ai_cost_spike',
        severity: 'critical',
        title: 'P95 AI Cost Spike Detected',
        message: `P95 AI cost per report spiked to $${latestP95?.toFixed(6)}, which is ${((latestP95 / avgP95 - 1) * 100)?.toFixed(1)}% above the 7-day average of $${avgP95?.toFixed(6)}`,
        metadata: {
          currentP95: latestP95,
          avgP95,
          spikePercentage: ((latestP95 / avgP95 - 1) * 100)
        }
      });

      return { success: true, data: [{ currentP95: latestP95, avgP95, spike: true }] };
    }

    return { success: true, data: [] };
  } catch (error) {
    console.error('Check AI cost spikes error:', error);
    return { success: false, error: error?.message };
  }
}

export async function checkUnusualUsagePatterns() {
  try {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString();

    const usersResult = await supabase
      ?.from('user_profiles')
      ?.select('id, email, full_name')
      ?.eq('role', 'client');

    if (usersResult?.error) throw usersResult?.error;

    const unusualUsers = [];

    for (const user of usersResult?.data || []) {
      const casesResult = await supabase
        ?.from('cases')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('user_id', user?.id)
        ?.gte('created_at', oneDayAgo);

      const casesCount = casesResult?.count || 0;

      if (casesCount > 10) {
        unusualUsers?.push({
          userId: user?.id,
          email: user?.email,
          casesCount
        });

        await createAlert({
          alertType: 'unusual_usage',
          severity: 'medium',
          title: `Unusual Usage Pattern: ${user?.email}`,
          message: `User ${user?.email} created ${casesCount} cases in the last 24 hours, which exceeds normal usage patterns`,
          metadata: {
            casesCount,
            timeframe: '24 hours'
          },
          userId: user?.id
        });
      }
    }

    return { success: true, data: unusualUsers };
  } catch (error) {
    console.error('Check unusual usage patterns error:', error);
    return { success: false, error: error?.message };
  }
}

export async function checkRiskPatterns() {
  try {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString();

    const failedPaymentsResult = await supabase
      ?.from('payments')
      ?.select('user_id, amount, failure_reason')
      ?.eq('payment_status', 'failed')
      ?.gte('created_at', oneDayAgo);

    if (failedPaymentsResult?.error) throw failedPaymentsResult?.error;

    const userFailureCounts = {};
    failedPaymentsResult?.data?.forEach(payment => {
      userFailureCounts[payment?.user_id] = (userFailureCounts?.[payment?.user_id] || 0) + 1;
    });

    for (const [userId, count] of Object.entries(userFailureCounts)) {
      if (count >= 3) {
        const userResult = await supabase
          ?.from('user_profiles')
          ?.select('email')
          ?.eq('id', userId)
          ?.single();

        await createAlert({
          alertType: 'risk_detected',
          severity: 'high',
          title: `Multiple Failed Payments: ${userResult?.data?.email}`,
          message: `User ${userResult?.data?.email} has ${count} failed payment attempts in the last 24 hours`,
          metadata: {
            failedPaymentCount: count,
            timeframe: '24 hours'
          },
          userId
        });
      }
    }

    return { success: true, data: Object.keys(userFailureCounts)?.length };
  } catch (error) {
    console.error('Check risk patterns error:', error);
    return { success: false, error: error?.message };
  }
}

export async function runAllAlertChecks() {
  try {
    const [negativeMargin, aiCostSpikes, unusualUsage, riskPatterns] = await Promise.all([
      checkNegativeMarginAccounts(),
      checkAICostSpikes(),
      checkUnusualUsagePatterns(),
      checkRiskPatterns()
    ]);

    return {
      success: true,
      data: {
        negativeMarginAccounts: negativeMargin?.data?.length || 0,
        aiCostSpikes: aiCostSpikes?.data?.length || 0,
        unusualUsage: unusualUsage?.data?.length || 0,
        riskPatterns: riskPatterns?.data || 0
      }
    };
  } catch (error) {
    console.error('Run all alert checks error:', error);
    return { success: false, error: error?.message };
  }
}

export async function getUnacknowledgedAlerts() {
  try {
    const { data, error } = await supabase
      ?.from('admin_alerts')
      ?.select('*')
      ?.eq('acknowledged', false)
      ?.order('created_at', { ascending: false })
      ?.limit(50);

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Get unacknowledged alerts error:', error);
    return { success: false, error: error?.message };
  }
}

export async function acknowledgeAlert(alertId, adminUserId) {
  try {
    const { data, error } = await supabase
      ?.from('admin_alerts')
      ?.update({
        acknowledged: true,
        acknowledged_by: adminUserId,
        acknowledged_at: new Date()?.toISOString()
      })
      ?.eq('id', alertId)
      ?.select()
      ?.single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Acknowledge alert error:', error);
    return { success: false, error: error?.message };
  }
}

export async function getAcknowledgedAlerts(limit = 50) {
  try {
    const { data, error } = await supabase
      ?.from('admin_alerts')
      ?.select('*')
      ?.eq('acknowledged', true)
      ?.order('acknowledged_at', { ascending: false })
      ?.limit(limit);

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Get acknowledged alerts error:', error);
    return { success: false, error: error?.message };
  }
}

export async function getAlertsByType(alertType, limit = 20) {
  try {
    const { data, error } = await supabase
      ?.from('admin_alerts')
      ?.select('*')
      ?.eq('alert_type', alertType)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Get alerts by type error:', error);
    return { success: false, error: error?.message };
  }
}